import { useMutation } from '@apollo/client/react';
import { Dialog, Box } from '@mui/material';
import React, { useState, useEffect } from 'react';

import AdditionalData from '../NewResourceModal/SingleResourceSection/AdditionalData';
import Basics from '../NewResourceModal/SingleResourceSection/Basics';
import Footer from '../NewResourceModal/SingleResourceSection/Footer';
import Header from '../NewResourceModal/SingleResourceSection/Header';
import { type ResourceDataType, type ResourceType } from '../NewResourceModal/SingleResourceSection/types';

import {
  type CompanyResource,
  CompanyResourceResourceTypeEnum,
  CompanyResourceSourceEnum,
  CreateCompanyResourceDocument,
  UpdateCompanyResourceDocument,
} from '@/graphql/generated/graphql';
import { GREY2 } from '@/theme/palette';
import { displayNotification } from '@/utils/notifications';

export interface CompanyResourceModalProps {
  open: boolean;
  handleClose: () => void;
  mode?: 'create' | 'view' | 'edit';
  resource?: CompanyResource | null;
  onSuccess?: () => void;
}

const TYPE_MAPPING_TO_UI: Record<string, ResourceType> = {
  human: 'Human',
  equipment: 'Equipment',
  material: 'Material',
  sub_contractor: 'Subcontractor',
};

const TYPE_MAPPING_TO_GQL: Record<ResourceType, CompanyResourceResourceTypeEnum> = {
  Human: CompanyResourceResourceTypeEnum.Human,
  Equipment: CompanyResourceResourceTypeEnum.Equipment,
  Material: CompanyResourceResourceTypeEnum.Material,
  Subcontractor: CompanyResourceResourceTypeEnum.SubContractor,
};

const DEFAULT_RESOURCE_DATA: ResourceDataType = {
  type: 'Human',
  name: '',
  source: 'Internal',
  code: '',
  contact_name: '',
  contact_phone: '',
  contact_email: '',
  notes: '',
};

const getInitialData = (resource?: CompanyResource | null): ResourceDataType => {
  if (!resource) return DEFAULT_RESOURCE_DATA;

  const uiType: ResourceType = TYPE_MAPPING_TO_UI[resource.resourceType] || 'Human';
  const uiSource: 'Internal' | 'External' =
    resource.source === CompanyResourceSourceEnum.External ? 'External' : 'Internal';

  return {
    type: uiType,
    name: resource.name || '',
    source: uiSource,
    code: resource.uniqueReference || '',
    contact_name: resource.resourceMeta?.pointOfContact || '',
    contact_phone: resource.resourceMeta?.primaryPhone || '',
    contact_email: resource.resourceMeta?.email || '',
    notes: resource.resourceMeta?.bio || '',
  };
};

const CompanyResourceModal: React.FC<CompanyResourceModalProps> = ({
  open,
  handleClose,
  mode = 'create',
  resource = null,
  onSuccess,
}) => {
  const [resourceData, setResourceData] = useState<ResourceDataType>(() => getInitialData(resource));

  useEffect(() => {
    setResourceData(getInitialData(resource));
  }, [resource, mode, open]);

  const handleDataChange = <K extends keyof ResourceDataType>(key: K, value: ResourceDataType[K]) => {
    setResourceData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const [createResource, { loading: creating }] = useMutation(CreateCompanyResourceDocument, {
    refetchQueries: ['getCompanyResourcesPaginated', 'getCompanyResources'],
    onCompleted: () => {
      displayNotification({
        type: 'success',
        text: 'Company Resource created successfully.',
      });
      onSuccess?.();
      handleClose();
    },
    onError: (err) => {
      displayNotification({
        type: 'error',
        text: err.message || 'Unable to create company resource.',
      });
    },
  });

  const [updateResource, { loading: updating }] = useMutation(UpdateCompanyResourceDocument, {
    refetchQueries: ['getCompanyResourcesPaginated', 'getCompanyResources'],
    onCompleted: () => {
      displayNotification({
        type: 'success',
        text: 'Company Resource updated successfully.',
      });
      onSuccess?.();
      handleClose();
    },
    onError: (err) => {
      displayNotification({
        type: 'error',
        text: err.message || 'Unable to update company resource.',
      });
    },
  });

  const handleSubmit = () => {
    if (mode === 'view') {
      handleClose();
      return;
    }

    const trimmedName = resourceData.name.trim();
    const trimmedCode = resourceData.code.trim();

    if (!trimmedName || !trimmedCode || !resourceData.source) {
      displayNotification({
        type: 'error',
        text: 'Please fill in all required fields (Resource name, Source, Unique reference / ID).',
      });
      return;
    }

    const sourceEnum =
      resourceData.source === 'External' ? CompanyResourceSourceEnum.External : CompanyResourceSourceEnum.Internal;

    const resourceTypeEnum = TYPE_MAPPING_TO_GQL[resourceData.type] || CompanyResourceResourceTypeEnum.Human;

    const resourceMeta = {
      pointOfContact: resourceData.contact_name.trim() || undefined,
      email: resourceData.contact_email.trim() || undefined,
      primaryPhone: resourceData.contact_phone.trim() || undefined,
      bio: resourceData.notes.trim() || undefined,
    };

    if (mode === 'create') {
      createResource({
        variables: {
          companyResource: {
            name: trimmedName,
            resourceType: resourceTypeEnum,
            uniqueReference: trimmedCode,
            source: sourceEnum,
            resourceMeta,
          },
        },
      });
    } else if (mode === 'edit' && resource?.id) {
      updateResource({
        variables: {
          companyResource: {
            id: resource.id,
            name: trimmedName,
            resourceType: resourceTypeEnum,
            uniqueReference: trimmedCode,
            source: sourceEnum,
            resourceMeta,
          },
        },
      });
    }
  };

  const isFormValid = !!resourceData.name.trim() && !!resourceData.code.trim() && !!resourceData.source;

  const headerTitle = mode === 'create' ? 'Create Resource' : mode === 'edit' ? 'Edit Resource' : 'Resource Details';

  const headerSubtitle =
    mode === 'create'
      ? 'Add a new resource to your organisation.'
      : mode === 'edit'
        ? 'Update resource details.'
        : 'View resource information.';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          overflow: 'hidden',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Header title={headerTitle} subtitle={headerSubtitle} onClose={handleClose} />

      <Box sx={{ flex: 1, overflowY: 'auto', backgroundColor: GREY2, pt: 1 }}>
        <Basics data={resourceData} setData={handleDataChange} disabled={mode === 'view'} />

        <AdditionalData data={resourceData} setData={handleDataChange} disabled={mode === 'view'} />
      </Box>

      <Footer
        onCancel={handleClose}
        onSubmit={handleSubmit}
        mode={mode}
        submitting={creating || updating}
        disabled={!isFormValid}
      />
    </Dialog>
  );
};

export default CompanyResourceModal;
